/**
 * DangoteCarbonStructuring — Execution Engine + Article 6 Readiness
 * Route: /corporate/carbon-structuring
 * Role: "How do we execute?" — Documentation workflow + Article 6 readiness.
 * Input: Selected opportunities from Carbon Strategy.
 *
 * Article 6 Readiness:
 *   - Classification (VCM / Art.6.2 / Art.6.4)
 *   - Readiness score (6 components, 100%)
 *   - LOA Readiness Checklist (NCCC)
 *   - Status tag
 *   - LoA Pack generation (draft PDF)
 *   - Mandatory disclaimer
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDangote } from "@/context/DangoteContext";
import { PageHeader, SectionCard } from "@/components/ds";
import {
  RefreshCw, CheckCircle2, XCircle, AlertTriangle, ArrowRight,
  FileText, ChevronDown, ChevronUp, Download, Info
} from "lucide-react";
import client from "@/api/client";

const STATUS_COLORS = {
  promoted_to_strategy:    "bg-blue-100 text-blue-700 border-blue-200",
  selected_for_execution:  "bg-teal-100 text-teal-700 border-teal-200",
  structuring_in_progress: "bg-amber-100 text-amber-700 border-amber-200",
  structuring_ready:       "bg-emerald-100 text-emerald-700 border-emerald-200",
  financing_review:        "bg-purple-100 text-purple-700 border-purple-200",
  financing_ready:         "bg-emerald-100 text-emerald-800 border-emerald-300",
  executed:                "bg-[#0F172A] text-white border-transparent",
};
const STATUS_LABELS = {
  promoted_to_strategy:"In Strategy", selected_for_execution:"Selected",
  structuring_in_progress:"Structuring", structuring_ready:"Ready",
  financing_review:"In Financing", financing_ready:"Finance Ready", executed:"Executed",
};

const READINESS_COMPONENTS = [
  { key: "project_desc",   label: "Project Description",       weight: 15 },
  { key: "baseline",       label: "Baseline Definition",        weight: 20 },
  { key: "additionality",  label: "Additionality Screening",    weight: 20 },
  { key: "mrv_plan",       label: "MRV Plan / Quantification",  weight: 20 },
  { key: "sdg_validation", label: "SDG Co-benefit Validation",  weight: 15 },
  { key: "evidence",       label: "Evidence Completeness",      weight: 10 },
];

function computeReadiness(opp) {
  const scores = {
    project_desc:  opp.description ? READINESS_COMPONENTS[0].weight : 0,
    baseline:      opp.baseline_type ? READINESS_COMPONENTS[1].weight : (opp.reduction_tco2e_yr > 0 ? 10 : 0),
    additionality: opp.vcm_eligible || opp.art6_eligible ? READINESS_COMPONENTS[2].weight : 8,
    mrv_plan:      opp.calculation_id ? READINESS_COMPONENTS[3].weight : (opp.reduction_tco2e_yr > 0 ? 10 : 0),
    sdg_validation:(opp.sdg_tags||[]).length > 0 ? READINESS_COMPONENTS[4].weight : 5,
    evidence:      opp.calc_hash ? READINESS_COMPONENTS[5].weight : 5,
  };
  return { scores, total: Object.values(scores).reduce((a,b)=>a+b,0) };
}

// Article 6 readiness computation
function computeArt6Readiness(opp) {
  const methodology  = (opp.vcm_eligible || opp.art6_eligible) ? 20 : 10;
  const baseline     = opp.baseline_type && opp.calc_hash ? 15 : (opp.reduction_tco2e_yr > 0 ? 8 : 0);
  const monitoring   = opp.reduction_tco2e_yr > 0 ? 15 : 0;
  const evidence     = opp.calc_hash ? 20 : 10;
  const safeguards   = 10; // placeholder
  const documentation = opp.description ? 20 : 10;
  const total = methodology + baseline + monitoring + evidence + safeguards + documentation;

  const components = [
    { label: "Methodology",    score: methodology,  max: 20 },
    { label: "Baseline",       score: baseline,     max: 15 },
    { label: "Monitoring",     score: monitoring,   max: 15 },
    { label: "Evidence",       score: evidence,     max: 20 },
    { label: "Safeguards",     score: safeguards,   max: 10 },
    { label: "Documentation",  score: documentation,max: 20 },
  ];

  const status = total >= 80 ? "Ready for submission"
    : total >= 60 ? "In preparation"
    : total >= 40 ? "Candidate"
    : "Not assessed";

  const statusCls = total >= 80
    ? "bg-emerald-100 text-emerald-700 border-emerald-200"
    : total >= 60
    ? "bg-blue-100 text-blue-700 border-blue-200"
    : total >= 40
    ? "bg-amber-100 text-amber-700 border-amber-200"
    : "bg-slate-100 text-slate-500 border-slate-200";

  return { total, components, status, statusCls };
}

function getLoaChecklist(opp) {
  return [
    { item: "Project description",       done: !!opp.description },
    { item: "Baseline defined",          done: !!(opp.baseline_type && opp.reduction_tco2e_yr > 0) },
    { item: "Additionality justification", done: !!(opp.vcm_eligible || opp.art6_eligible) },
    { item: "Monitoring plan",           done: !!(opp.reduction_tco2e_yr > 0 && opp.calc_hash) },
    { item: "Safeguards",                done: false },  // placeholder
    { item: "Stakeholder consultation",  done: false },  // placeholder
    { item: "Emissions quantified",      done: !!(opp.reduction_tco2e_yr > 0) },
  ];
}

// Article 6 section — per opportunity card
function Art6Section({ opp }) {
  const [expanded, setExpanded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const art6 = computeArt6Readiness(opp);
  const checklist = getLoaChecklist(opp);
  const completedCount = checklist.filter(c => c.done).length;

  const isVcm  = opp.vcm_eligible;
  const isArt62 = opp.art6_eligible && (opp.reduction_tco2e_yr || 0) >= 100_000;
  const isArt64 = opp.art6_eligible;

  const handleLoaPack = async () => {
    setGenerating(true);
    try {
      const resp = await client.get(`/dangote/pdf/loa/${opp.opp_id}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `Dangote_Art6_LoA_${opp.opp_id}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      alert("LoA Pack generation failed. Try again.");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="mt-4 border-t border-slate-100 pt-4" data-testid={`art6-${opp.opp_id}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <p className="text-xs font-bold text-slate-600 uppercase tracking-wide">Article 6 Readiness</p>
          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${art6.statusCls}`}>
            {art6.status}
          </span>
        </div>
        <button onClick={() => setExpanded(v => !v)} className="text-slate-400 hover:text-slate-700">
          {expanded ? <ChevronUp size={13}/> : <ChevronDown size={13}/>}
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden mb-3">
        <div className={`h-full rounded-full transition-all ${
          art6.total >= 80 ? "bg-emerald-500" : art6.total >= 60 ? "bg-blue-400" : "bg-amber-400"
        }`} style={{width:`${art6.total}%`}} />
      </div>
      <p className="text-[9px] text-slate-400 mb-3">Article 6 readiness: {art6.total}/100 · LOA items complete: {completedCount}/7</p>

      {/* Classification badges */}
      <div className="flex gap-1.5 flex-wrap mb-3">
        {isVcm  && <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-teal-100 text-teal-700 border border-teal-200">VCM</span>}
        {isArt62 && <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-700 border border-blue-200">Art.6.2 Candidate</span>}
        {isArt64 && <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-purple-100 text-purple-700 border border-purple-200">Art.6.4 Candidate</span>}
        {!isVcm && !isArt62 && !isArt64 && (
          <span className="text-[9px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-500 border border-slate-200">Internal</span>
        )}
      </div>

      {expanded && (
        <div className="space-y-3">
          {/* Score breakdown */}
          <div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">Score Breakdown</p>
            <div className="space-y-1">
              {art6.components.map(c => (
                <div key={c.label} className="flex items-center justify-between text-xs">
                  <span className="text-slate-500">{c.label}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${c.score >= c.max ? "bg-emerald-500" : "bg-amber-400"}`}
                        style={{width:`${(c.score/c.max)*100}%`}} />
                    </div>
                    <span className={`font-bold text-[10px] w-8 text-right ${c.score >= c.max ? "text-emerald-600" : "text-amber-600"}`}>
                      {c.score}/{c.max}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* LOA Checklist */}
          <div>
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">LOA Readiness Checklist (NCCC)</p>
            <div className="space-y-0.5">
              {checklist.map(c => (
                <div key={c.item} className="flex items-center gap-1.5 text-xs py-0.5">
                  {c.done
                    ? <CheckCircle2 size={11} className="text-emerald-500 flex-shrink-0"/>
                    : <XCircle size={11} className="text-red-400 flex-shrink-0"/>}
                  <span className={c.done ? "text-slate-700" : "text-slate-400"}>{c.item}</span>
                  {!c.done && <span className="text-[9px] text-red-500 font-semibold ml-auto">missing</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* LoA Pack button */}
      <button onClick={handleLoaPack} disabled={generating}
        className="w-full mt-3 flex items-center justify-center gap-1.5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl disabled:opacity-50 transition-colors"
        data-testid={`loa-btn-${opp.opp_id}`}>
        {generating
          ? <><RefreshCw size={11} className="animate-spin"/> Generating...</>
          : <><Download size={11}/> Generate Article 6 LoA Pack (Draft)</>}
      </button>

      {/* Disclaimer */}
      <div className="mt-2 flex items-start gap-1.5">
        <Info size={10} className="text-slate-400 flex-shrink-0 mt-0.5" />
        <p className="text-[9px] text-slate-400 italic">
          Article 6 readiness only. Not approved. Subject to host country authorization (NCCC).
        </p>
      </div>
    </div>
  );
}

function ReadinessCard({ opp, onTransition, transitioning }) {
  const { scores, total } = computeReadiness(opp);
  const canSendToFinancing = ["structuring_in_progress","structuring_ready"].includes(opp.status);
  const canSendToStructuring = opp.status === "promoted_to_strategy";
  const art6 = computeArt6Readiness(opp);

  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-testid={`structuring-card-${opp.opp_id}`}>
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${STATUS_COLORS[opp.status]||STATUS_COLORS.selected_for_execution}`}>
                {STATUS_LABELS[opp.status]||opp.status}
              </span>
              {(opp.flags||[]).includes("quick_win") && (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-teal-100 text-teal-700 border border-teal-200">Quick Win</span>
              )}
              {/* Art.6 status badge */}
              <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border ${art6.statusCls}`}>
                Art.6: {art6.status}
              </span>
            </div>
            <p className="text-sm font-bold text-slate-900">{opp.name}</p>
            <p className="text-xs text-slate-400">{opp.bu_name} · {opp.asset_id}</p>
          </div>
          {/* Readiness score */}
          <div className="text-center flex-shrink-0">
            <div className="relative w-14 h-14">
              <svg className="w-14 h-14 -rotate-90" viewBox="0 0 44 44">
                <circle cx="22" cy="22" r="18" fill="none" stroke="#f1f5f9" strokeWidth="4"/>
                <circle cx="22" cy="22" r="18" fill="none"
                  stroke={total>=80?"#10b981":total>=50?"#f59e0b":"#ef4444"} strokeWidth="4"
                  strokeDasharray={`${113 * total / 100} 113`} strokeLinecap="round"/>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <p className={`text-sm font-bold ${total>=80?"text-emerald-700":total>=50?"text-amber-700":"text-red-700"}`}>{total}</p>
              </div>
            </div>
            <p className="text-[9px] text-slate-400 mt-0.5">PDD ready</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-3">
          <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${total>=80?"bg-emerald-500":total>=50?"bg-amber-400":"bg-red-400"}`}
              style={{width:`${total}%`}} />
          </div>
          <p className="text-[9px] text-slate-400 mt-0.5">Structuring progress: {total}/100</p>
        </div>
      </div>

      <div className="px-5 py-4">
        {/* Documentation components */}
        <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Documentation Components</p>
        <div className="space-y-1.5">
          {READINESS_COMPONENTS.map(comp => {
            const score   = scores[comp.key] || 0;
            const isDone  = score >= comp.weight;
            const isPartial = score > 0 && !isDone;
            return (
              <div key={comp.key} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5">
                  {isDone
                    ? <CheckCircle2 size={11} className="text-emerald-500 flex-shrink-0"/>
                    : isPartial
                    ? <AlertTriangle size={11} className="text-amber-500 flex-shrink-0"/>
                    : <XCircle size={11} className="text-red-400 flex-shrink-0"/>}
                  <span className={`${isDone?"text-slate-700":isPartial?"text-amber-700":"text-slate-400"}`}>{comp.label}</span>
                </div>
                <span className={`font-bold ${isDone?"text-emerald-600":isPartial?"text-amber-600":"text-slate-300"}`}>
                  {score}/{comp.weight}
                </span>
              </div>
            );
          })}
        </div>

        {/* Key data */}
        <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-3 gap-2 text-xs">
          {[
            ["Reduction",  `${Number(opp.reduction_tco2e_yr||0).toLocaleString()} tCO₂e/yr`],
            ["IRR",        `${opp.irr_pct||"—"}%`],
            ["Pathway",    (opp.pathway||"internal").toUpperCase()],
          ].map(([l,v]) => (
            <div key={l} className="bg-slate-50 rounded-lg px-2 py-1.5">
              <p className="text-slate-400">{l}</p>
              <p className="font-bold text-slate-900">{v}</p>
            </div>
          ))}
        </div>

        {/* Actions */}
        {canSendToStructuring && (
          <button onClick={() => onTransition(opp.opp_id, "structuring_in_progress")}
            disabled={transitioning}
            className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 disabled:opacity-50"
            data-testid={`send-to-structuring-${opp.opp_id}`}>
            {transitioning ? <RefreshCw size={11} className="animate-spin"/> : <ArrowRight size={11}/>}
            Move to Structuring
          </button>
        )}
        {canSendToFinancing && (
          <button onClick={() => onTransition(opp.opp_id, "financing_review")}
            disabled={transitioning}
            className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 disabled:opacity-50"
            data-testid={`send-to-financing-${opp.opp_id}`}>
            {transitioning ? <RefreshCw size={11} className="animate-spin"/> : <ArrowRight size={11}/>}
            Send to Financing
          </button>
        )}
        {(opp.status === "financing_review" || opp.status === "financing_ready") && (
          <div className="mt-3 flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
            <CheckCircle2 size={11}/> In Financing pipeline
          </div>
        )}

        {/* Article 6 Readiness Section */}
        <Art6Section opp={opp} />
      </div>
    </div>
  );
}

export default function DangoteCarbonStructuring() {
  const navigate = useNavigate();
  const { selectedPeriod, period, vbCache, vbLoading, invalidateVb } = useDangote();
  const [transitioning, setTrans] = useState(false);
  const [grouping, setGrouping] = useState("structuring");

  const vbData  = selectedPeriod ? (vbCache[selectedPeriod] || null) : null;
  const loading = vbLoading && !vbData;

  const handleTransition = async (opp_id, newStatus) => {
    setTrans(true);
    await client.post(`/dangote/opportunities/${opp_id}/transition`, { status: newStatus });
    invalidateVb(selectedPeriod);
    setTrans(false);
  };

  const allOpps   = vbData?.opportunities || [];
  const byStatus  = vbData?.by_status     || {};

  const GROUPINGS = {
    structuring:  allOpps.filter(o => ["structuring_in_progress","structuring_ready"].includes(o.status)),
    promoted:     allOpps.filter(o => o.status === "promoted_to_strategy"),
    all_active:   allOpps.filter(o => !["identified","deprioritized","executed"].includes(o.status)),
    all:          allOpps,
  };
  const displayed = GROUPINGS[grouping] || GROUPINGS.all_active;
  const avgReadiness = displayed.length > 0
    ? Math.round(displayed.reduce((s,o) => s + computeReadiness(o).total, 0) / displayed.length)
    : 0;

  if (loading) return (
    <div className="p-6 space-y-4">{[1,2,3].map(i=><div key={i} className="h-14 bg-slate-100 rounded-xl animate-pulse"/>)}</div>
  );

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Dangote Industries</p>
          <PageHeader title="Carbon Structuring" />
          <p className="text-xs text-slate-400 mt-0.5">
            Documentation + Article 6 readiness · Input from Carbon Strategy · {period?.label}
          </p>
        </div>
        <span className="text-xs font-bold px-2 py-0.5 rounded-full border bg-amber-100 text-amber-700 border-amber-200">
          Avg Readiness: {avgReadiness}/100
        </span>
      </div>

      {/* Article 6 Disclaimer */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 flex items-start gap-2">
        <Info size={13} className="text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-blue-700">
          <strong>Article 6 readiness and documentation preparation — not approval or issuance.</strong>
          {" "}Host country authorization (NCCC) is required before any Article 6 ITMO can be issued.
          LoA Pack outputs are draft documents for internal review only.
        </p>
      </div>

      {/* Pipeline status */}
      <div className="grid grid-cols-4 gap-3">
        {[
          ["Structuring",  (byStatus.structuring_in_progress||0)+(byStatus.structuring_ready||0), "text-amber-700"],
          ["Ready",        byStatus.structuring_ready||0,  "text-emerald-700"],
          ["In Financing", (byStatus.financing_review||0)+(byStatus.financing_ready||0), "text-purple-700"],
          ["Executed",     byStatus.executed||0, "text-teal-700"],
        ].map(([l,v,c]) => (
          <div key={l} className="bg-white border border-slate-200 rounded-xl p-3 text-center">
            <p className="text-xs text-slate-400">{l}</p>
            <p className={`text-xl font-bold ${c}`}>{v}</p>
          </div>
        ))}
      </div>

      {/* View selector */}
      <div className="flex gap-2 flex-wrap">
        {[
          ["structuring", `In Structuring (${GROUPINGS.structuring.length})`],
          ["promoted",    `Promoted (${GROUPINGS.promoted.length})`],
          ["all_active",  `All Active (${GROUPINGS.all_active.length})`],
          ["all",         `All (${allOpps.length})`],
        ].map(([k,l]) => (
          <button key={k} onClick={() => setGrouping(k)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
              grouping===k ? "bg-[#0F172A] text-white border-transparent" : "bg-white border-slate-200 text-slate-500"
            }`}>{l}</button>
        ))}
      </div>

      {displayed.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
          <FileText size={20} className="text-slate-400 mx-auto mb-2"/>
          <p className="text-slate-500 text-sm">No projects in this view</p>
          <button onClick={() => navigate("/corporate/carbon-strategy")}
            className="mt-3 text-xs text-teal-600 underline">Go to Carbon Strategy →</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {displayed.map(o => (
            <ReadinessCard key={o.opp_id} opp={o}
              onTransition={handleTransition} transitioning={transitioning} />
          ))}
        </div>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
        <p className="text-xs text-amber-700 italic">
          Readiness scores are indicative. Formal PDD preparation, VVB validation, and registry registration
          require external specialists. Article 6 requires host-country DNA authorisation.
          Operational Value excludes carbon revenue in DSCR calculations.
        </p>
      </div>
    </div>
  );
}
